/**
 * Helpers para imágenes responsive · srcset/sizes.
 *
 * El backend devuelve `variants: { "480": url, "960": url, ... }` por
 * cada media asset.  Para imágenes legacy en `/api/uploads/looks/after/`
 * existen las convenciones `<id>-thumb.webp` (500w), `<id>-medium.webp`
 * (1000w), `<id>-full.webp` (1600w).
 */

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

/**
 * Construye un string srcSet a partir de variants {width: url}.
 * Devuelve '' si no hay variants válidas (entonces el <img> usa solo `src`).
 */
export function buildSrcSet(variants) {
  if (!variants || typeof variants !== 'object') return '';
  const entries = Object.entries(variants)
    .map(([w, url]) => [parseInt(w, 10), url])
    .filter(([w, u]) => Number.isFinite(w) && w > 0 && typeof u === 'string' && u);
  if (entries.length === 0) return '';
  entries.sort((a, b) => a[0] - b[0]);
  return entries
    .map(([w, u]) => `${u.startsWith('http') ? u : `${API_BASE}${u}`} ${w}w`)
    .join(', ');
}

/**
 * Construye srcSet para imágenes legacy de looks/after con la convención
 * `<id>-thumb.webp` / `<id>-medium.webp` / `<id>-full.webp`.
 *
 * Si `format='avif'`, devuelve el srcset apuntando a los .avif equivalentes
 * (que el backend genera con `legacy_avif.ensure_legacy_avif()`).
 */
export function buildLooksSrcSet(prefix /* e.g. /api/uploads/looks/after/01 */, format = 'webp') {
  if (!prefix) return '';
  const base = prefix.startsWith('http') ? prefix : `${API_BASE}${prefix}`;
  const ext = format === 'avif' ? 'avif' : 'webp';
  return [
    `${base}-thumb.${ext} 500w`,
    `${base}-medium.${ext} 1000w`,
    `${base}-full.${ext} 1600w`,
  ].join(', ');
}

/**
 * Detecta el patrón `<...>/<id>-{thumb|medium|full}.webp` en `src` y devuelve
 * el srcSet equivalente con las 3 variantes legacy.  Devuelve '' si no hay match.
 *
 * Si `format='avif'`, devuelve los .avif (que conviven con los .webp gracias a
 * legacy_avif.ensure_legacy_avif()).
 */
export function autoLegacySrcSet(src, format = 'webp') {
  if (typeof src !== 'string') return '';
  const m = src.match(/^(.*\/[^/]+)-(?:thumb|medium|full)\.webp(\?.*)?$/);
  if (!m) return '';
  const prefix = m[1];
  const qs = m[2] || '';
  const norm = prefix.startsWith('http') ? prefix : `${API_BASE}${prefix}`;
  const ext = format === 'avif' ? 'avif' : 'webp';
  return [
    `${norm}-thumb.${ext}${qs} 500w`,
    `${norm}-medium.${ext}${qs} 1000w`,
    `${norm}-full.${ext}${qs} 1600w`,
  ].join(', ');
}

/** Default sizes attribute para hero / showcases full-bleed. */
export const FULL_BLEED_SIZES = '100vw';
/** Default sizes para galerías 2-4 cols. */
export const GALLERY_SIZES = '(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw';
/** Default sizes para imágenes a media columna. */
export const HALF_SIZES = '(min-width: 768px) 50vw, 100vw';
